import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Sparkles, PlayCircle, Activity, Send as SendIcon } from "lucide-react";

const BUCKET_STYLES = {
  healthy:          "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  needs_attention:  "bg-amber-500/10 text-amber-300 border-amber-500/30",
  at_risk:          "bg-rose-500/10 text-rose-300 border-rose-500/30",
  inactive:         "bg-slate-500/10 text-slate-400 border-slate-700",
};

export function GrowthActivation() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [drafting, setDrafting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/growth/activation");
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const scan = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/growth/lanes/activation/scan");
      toast.success(`Scanned ${data.scanned} companies — ${data.stalled} stalled, ${data.approaching_trial_end} approaching end, ${data.activated_at_risk} at risk.`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setRunning(false); }
  };

  const draftNudges = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post("/growth/activation/draft-nudges", { limit: 50 });
      if (data.drafted > 0) {
        toast.success(`Drafted ${data.drafted} nudges — review them in Outreach Center.`, { duration: 5000 });
      } else if (data.skipped_existing > 0) {
        toast.info(`No new drafts — ${data.skipped_existing} already in the queue.`);
      } else if (data.skipped_no_contact > 0) {
        toast.warning(`Skipped ${data.skipped_no_contact} companies (no admin email on file).`);
      } else {
        toast.info("Nothing to nudge — everyone's healthy. Run a scan first if that seems off.");
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setDrafting(false); }
  };

  return (
    <div className="p-6 md:p-8 space-y-5 max-w-7xl mx-auto" data-testid="growth-activation">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-white tracking-tight flex items-center gap-2"><Sparkles className="h-7 w-7 text-indigo-400" /> Customer Activation Queue</h1>
          <p className="text-slate-400 text-sm mt-1">Reads existing PPT data. 15 events feed the Account Health Score. Converting an existing trial is 5-10× cheaper than finding a new one.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={draftNudges} disabled={drafting} className="bg-indigo-600 hover:bg-indigo-500" data-testid="draft-nudges-btn">
            {drafting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <SendIcon className="h-4 w-4 mr-1" />}
            Draft nudges for all patterned trials
          </Button>
          <Button onClick={scan} disabled={running} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold" data-testid="scan-activation-btn">
            {running ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <PlayCircle className="h-4 w-4 mr-1" />}
            Run activation scan
          </Button>
        </div>
      </div>

      {loading ? <div className="text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</div>
      : items.length === 0 ? (
        <Card className="bg-slate-900/60 border-slate-800"><CardContent className="p-10 text-center text-slate-400 text-sm">No scores yet. Click <b>Run activation scan</b> to score every company.</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {items.map((r) => (
            <Card key={r.company_id} className="bg-slate-900/60 border-slate-800" data-testid={`activation-row-${r.company_id}`}>
              <CardContent className="p-4 flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] font-extrabold uppercase tracking-widest border rounded px-1.5 py-0.5 ${BUCKET_STYLES[r.bucket] || BUCKET_STYLES.inactive}`}>{r.bucket}</span>
                    <span className="text-[10px] text-slate-500">{r.subscription_status || "no sub"}</span>
                    {r.days_to_trial_end != null && r.days_to_trial_end >= 0 && <span className="text-[10px] text-amber-300 font-bold">{r.days_to_trial_end}d trial left</span>}
                  </div>
                  <p className="text-white font-bold mt-1">{r.company_name}</p>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {(r.patterns || []).map((p) => (
                      <span key={p} className="text-[10px] font-extrabold uppercase tracking-widest text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-1.5 py-0.5">{p.replace(/_/g, " ")}</span>
                    ))}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-3xl font-extrabold text-indigo-300">{Math.round(r.score_pct)}</p>
                  <p className="text-[10px] uppercase tracking-widest text-slate-500">health %</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export function GrowthAgentLog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/growth/agent-log");
        setItems(data.items || []);
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="p-6 md:p-8 space-y-5 max-w-6xl mx-auto" data-testid="growth-agent-log">
      <div>
        <h1 className="text-3xl font-extrabold text-white tracking-tight flex items-center gap-2"><Activity className="h-7 w-7 text-indigo-400" /> Agent Activity</h1>
        <p className="text-slate-400 text-sm mt-1">Append-only log of every AI action. Searchable, exportable.</p>
      </div>
      {loading ? <div className="text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</div>
      : items.length === 0 ? (
        <Card className="bg-slate-900/60 border-slate-800"><CardContent className="p-10 text-center text-slate-400 text-sm">Nothing logged yet.</CardContent></Card>
      ) : (
        <div className="rounded-xl border border-slate-800 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-slate-500 text-[10px] uppercase tracking-widest">
              <tr>
                <th className="text-left px-3 py-2">When</th>
                <th className="text-left px-3 py-2">Agent</th>
                <th className="text-left px-3 py-2">Action</th>
                <th className="text-left px-3 py-2">Object</th>
                <th className="text-right px-3 py-2">Conf</th>
                <th className="text-left px-3 py-2">Result</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-t border-slate-800 bg-slate-950" data-testid={`log-row-${r.id}`}>
                  <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{new Date(r.at).toLocaleString()}</td>
                  <td className="px-3 py-2 text-indigo-300">{r.agent}</td>
                  <td className="px-3 py-2 text-white font-semibold">{r.action}</td>
                  <td className="px-3 py-2 text-slate-400">{r.object_type ? `${r.object_type}#${(r.object_id || "").slice(0, 6)}` : "—"}</td>
                  <td className="px-3 py-2 text-right text-slate-400">{r.confidence != null ? Math.round(r.confidence * 100) : "—"}</td>
                  <td className={`px-3 py-2 font-extrabold ${r.result === "ok" ? "text-emerald-300" : r.result === "warn" ? "text-amber-300" : "text-rose-300"}`}>{r.result}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
